import { IonList } from "@ionic/react";
import { ComponentProps, useRef, useState } from "react";
import { useIntl } from "react-intl";

import ProviderForm from "@components/forms/ProviderForm";
import Modal, { ModalElement } from "@components/Modal";
import TrackedItem from "@components/trackers/TrackedItem";
import TrackedItemLine from "@components/trackers/TrackedItemLine";
import Tracker from "@components/trackers/Tracker";
import styles from "@components/trackers/Tracker.module.css";
import TrackerItemModal from "@components/trackers/TrackerItemModal";
import useDestroyProvider from "@hooks/mutations/userDestroyProvider";
import useOpener from "@hooks/useOpener";
import { GetJourneyWithPlanQuery, ProviderFragment } from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";

type Props = {
  journey: GetJourneyWithPlanQuery["journey"];
};

type ProviderProps = {
  handleDestroy: (p: ProviderFragment) => void;
  handleEdit: (p: ProviderFragment) => void;
  provider: ProviderFragment;
};

const Provider = ({ handleDestroy, handleEdit, provider }: ProviderProps) => {
  const intl = useIntl();
  const opener = useOpener();

  const itemProps: ComponentProps<typeof TrackedItemLine>[] = [];
  if (provider.practiceName) {
    itemProps.push({ description: provider.practiceName, icon: "house" });
  }
  if (provider.address) {
    itemProps.push({ description: provider.address, icon: "locationDot" });
  }
  if (provider.phone) {
    itemProps.push({
      description: `${provider.phone} ${intl.formatMessage({ id: "components.Providers.phone" })}`,
      icon: "phone"
    });
  }
  if (provider.fax) {
    itemProps.push({
      description: `${provider.fax} ${intl.formatMessage({ id: "components.Providers.fax" })}`,
      icon: "hashtag"
    });
  }
  if (provider.website) {
    itemProps.push({ description: provider.website, icon: "globe" });
  }
  if (provider.patientPortal) {
    itemProps.push({ description: provider.patientPortal, icon: "user" });
  }

  return (
    <>
      <TrackedItem
        onClick={opener.open}
        subtitle={intl.formatMessage({ id: `models.provider.kindOptions.${provider.kind}` })}
        title={provider.name}
      />
      <TrackerItemModal
        actions={[
          {
            handler: () => handleEdit(provider),
            icon: "edit",
            labelKey: "dictionary.edit"
          },
          {
            handler: () => handleDestroy(provider),
            icon: "trash",
            labelKey: "dictionary.remove"
          }
        ]}
        notes={provider.notes}
        opener={opener}
        subtitle={intl.formatMessage({ id: `models.provider.kindOptions.${provider.kind}` })}
        title={provider.name}
      >
        <IonList className="ion-no-padding">
          {itemProps.map((props, index) => (
            // array index key is actually fine here since the array is static and it's the most convenient thing to use
            // eslint-disable-next-line react/no-array-index-key
            <TrackedItemLine key={index} {...props} color={index % 2 === 0 ? "neutral" : "white"} />
          ))}
        </IonList>
      </TrackerItemModal>
    </>
  );
};

const Providers = ({ journey }: Props) => {
  const intl = useIntl();
  const addOpener = useOpener();
  const providerModalRef = useRef<ModalElement>(null);
  const [editingProvider, setEditingProvider] = useState<ProviderFragment | undefined>(undefined);

  const handleProviderError = () => {
    if (providerModalRef.current) {
      providerModalRef.current.scrollToTop();
    }
  };

  const destroyProvider = useDestroyProvider(journey.id);

  return (
    <div className={styles.trackerContainer}>
      <Tracker
        handleAdd={() => {
          setEditingProvider(undefined);
          addOpener.open();
        }}
        kind="provider"
      >
        <div className={styles.items}>
          {sortByTimestamp(journey.providers, provider => provider.createdAt).map(provider => (
            <Provider
              handleDestroy={p => {
                destroyProvider({
                  id: p.id
                });
              }}
              handleEdit={p => {
                setEditingProvider(p);
                addOpener.open();
              }}
              key={provider.id}
              provider={provider}
            />
          ))}
        </div>
      </Tracker>

      <Modal
        opener={addOpener}
        ref={providerModalRef}
        title={
          editingProvider
            ? intl.formatMessage({ id: "models.careProfile.updateProvider" })
            : intl.formatMessage({ id: "models.careProfile.addNewProvider" })
        }
      >
        {({ modalDisplayed }) => (
          <ProviderForm
            autofocus={modalDisplayed}
            journeyId={journey.id}
            kindEditable={!editingProvider}
            onCancel={addOpener.close}
            onError={handleProviderError}
            onSuccess={addOpener.close}
            provider={editingProvider}
          />
        )}
      </Modal>
    </div>
  );
};

export default Providers;
